import { Button, Card, Col, Divider, Flex, Row, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useStopwatch } from 'react-timer-hook';

//components
import MapComponent from 'components/map/map';
import BackButton from 'components/backButton/backButton';
import DoughnutComponent from 'modules/main/actions/assemblies/components/dougnet';
import PageTitle from 'components/title/title';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import DisabledElement from 'components/disabledElement/disabledElement';
import FilePreview from 'components/filePreview/filePreview';
import StatusComponent from 'components/status/status';

// services
import NotificationService from 'core/services/notification';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';

//models
import { CommitteeMeeting } from '../models/meetings';

//styles
import Icon from 'utils/icon';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import TableTotal from 'components/tableTotal/tableTotal';
import NoData from 'components/noData/noData';
import {
	onCloseCommitteeMeeting,
	onEndCommitteeMeetingVoting,
	onGetCommitteeMeetingAttendees,
	onGetCommitteeMeetingById,
	onPublishCommitteeMeeting,
} from '../services/meetingServices';
import { CommitteeAttendee } from '../models/attendance';
import dayjs from 'dayjs';

const CommitteeMeetingViewPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const {
		seconds: votingSeconds,
		minutes: votingMinutes,
		hours: votingHours,
		reset: votingTimerReset,
	} = useStopwatch({
		autoStart: false,
	});

	const {
		seconds: meetingSeconds,
		minutes: meetingMinutes,
		hours: meetingHours,
		reset: meetingTimerReset,
	} = useStopwatch({
		autoStart: false,
	});

	const { committeeId, meetingId } = useParams();

	const [meeting, setMeeting] = useState<CommitteeMeeting>();

	const [isAttendeeLoading, setIsAttendeeLoading] = useState<boolean>(false);
	const [attendees, setAttendees] = useState<CommitteeAttendee[]>([]);
	const [totalAttendeesRecords, setTotalAttendeesRecords] = useState<number>(0);
	const [currentAttendeePage, setCurrentAttendeePage] = useState<number>(1);

	const [isLoading, setIsLoading] = useState(true);
	const [isPublishing, setIsPublishing] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [closeVoteModalOpen, setCloseVoteModalOpen] = useState(false);
	const [endMeetingModalOpen, setEndMeetingModalOpen] = useState(false);

	const attendeesColumns: TableProps<CommitteeAttendee>['columns'] = [
		{
			title: t('COMMON.NAME'),
			dataIndex: '',
			key: 'name',
			// render: (_, row) => row?.board_member?.name,
		},
		{
			title: t('ACTIONS.POSITION'),
			dataIndex: '',
			key: 'position',
			// render: (_, row) => t(`ACTIONS.${row?.board_member?.position.toUpperCase()}`),
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: '',
			key: 'type',
			// render: (_, row) => t(`ACTIONS.${row?.board_member?.type.toUpperCase()}`),
		},
		{
			title: t('ACTIONS.ATTEND_TYPE'),
			dataIndex: 'attend_by',
			key: 'attend_by',
			render: (attend_by) => (
				<StatusComponent
					text={attend_by === 'CompanyUser' ? t('ACTIONS.COMPANY') : t('COMMON.SHAREHOLDER')}
					color={attend_by === 'CompanyUser' ? 'blue' : 'green'}
				/>
			),
		},
	];

	useEffect(() => {
		getMeetingDetails();
	}, []);

	const getMeetingDetails = async () => {
		try {
			setIsLoading(true);

			const data = await onGetCommitteeMeetingById(committeeId!, meetingId!);
			setMeeting(data);

			if (data.meeting_start_at) {
				const elapsedSeconds = Math.floor((Date.now() - dayjs(data.meeting_start_at).toDate().getTime()) / 1000);
				const offset = new Date(Date.now() + elapsedSeconds * 1000);
				meetingTimerReset(offset);
			}

			if (data.voting_start_at) {
				const elapsedSeconds = Math.floor((Date.now() - dayjs(data.voting_start_at).toDate().getTime()) / 1000);
				const offset = new Date(Date.now() + elapsedSeconds * 1000);
				votingTimerReset(offset);
			}
			getAttendees();

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/boards/committees/meetings');
		}
	};

	const getAttendees = async (page = 1) => {
		try {
			if (meeting!.status === 'Scheduled') return;

			setIsLoading(true);

			const response = await onGetCommitteeMeetingAttendees(committeeId!, meetingId!, page);
			setAttendees(response.committee_meeting_attendances);
			setTotalAttendeesRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	const closeVoteHandler = async () => {
		try {
			setIsSubmitting(true);
			await onEndCommitteeMeetingVoting(committeeId!, meeting?.id!);
			NotificationService.success(t('ACTIONS.VOTING_CLOSING_SUCCESS'));
			setCloseVoteModalOpen(false);
			setIsSubmitting(false);
			getMeetingDetails();
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const endMeetingHandler = async () => {
		try {
			setIsSubmitting(true);
			await onCloseCommitteeMeeting(committeeId!, meeting?.id!);
			NotificationService.success(t('ACTIONS.MEETING_ENDED_SUCCESSFULLY'));
			setEndMeetingModalOpen(false);
			setIsSubmitting(false);
			getMeetingDetails();
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const publishHandler = async () => {
		try {
			setIsPublishing(true);
			await onPublishCommitteeMeeting(committeeId!, meeting?.id!);
			NotificationService.success(t('ACTIONS.MEETING_PUBLISHED_SUCCESSFULLY'));
			setIsPublishing(false);
			getMeetingDetails();
		} catch (error: any) {
			setIsPublishing(false);

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<PageTitle name={t('PAGES.BOARD_MEETING')} />

			<BackButton />

			<Card>
				{isLoading ? (
					<LoadingComponent />
				) : (
					<>
						<Flex justify="space-between" align="flex-start" className="mb-2">
							<div className="mb-3">
								<Flex gap={20} align="center" className="mb-2">
									<h5 style={{ fontWeight: 500, marginBottom: 0 }}>
										{t('ACTIONS.MEETING')} {meeting?.id}
									</h5>

									<StatusComponent text={t(`COMMON.${meeting?.status.toUpperCase()}`)} small color={StatusColors[meeting?.status as string]} />
								</Flex>

								<p style={{ marginBottom: 0, color: '#555' }}>{meeting?.created_at}</p>
							</div>
						</Flex>

						<Row gutter={[16, 16]}>
							<Col span={24} md={8}>
								<h6 className="fw-medium mb-4">
									{t('ACTIONS.MEETING_TYPE')}: {isRTL() ? meeting?.translated_type.ar : meeting?.translated_type.en}
								</h6>

								<DisabledElement label={t('ACTIONS.STARTING_DATE')} value={meeting?.date} style={{ width: 'max-content', paddingInlineEnd: 50 }} />
							</Col>

							<Col span={24} md={8}>
								<div className="mb-3">
									<h6 className="fw-medium mb-3">{t('ACTIONS.ATTENDANCE_SUMMARY')}</h6>
									<Flex gap={20}>
										<DisabledElement value={meeting?.attendances_count} addonAfter={<span>{t('ACTIONS.MEMBERS')}</span>} />

										<DisabledElement value={meeting?.guests_count} addonAfter={<span>{t('ACTIONS.GUESTS')}</span>} />
									</Flex>
								</div>

								{meeting?.type === 'Voting' ? (
									<DisabledElement label={t('ACTIONS.VOTING_DURATION')} value={meeting?.duration_minutes} />
								) : (
									<div>
										<h6 className="mb-3 fw-medium">{t('ACTIONS.LOCATION')}</h6>

										<Flex gap={30} align="center">
											{(meeting?.location_type.includes('Online') || meeting?.location_type === 'Both') && (
												<Flex gap={5} align="center">
													<Icon name="squareCheck" color="var(--primary)" />
													<p className="mb-0">{t('ACTIONS.ONLINE')}</p>
												</Flex>
											)}

											{(meeting?.location_type.includes('Physical') || meeting?.location_type === 'Both') && (
												<Flex gap={5} align="center">
													<Icon name="squareCheck" color="var(--primary)" />
													<p className="mb-0">{t('ACTIONS.PHYSICAL')}</p>
												</Flex>
											)}
										</Flex>
									</div>
								)}
							</Col>

							<Col span={24} md={8}>
								<DoughnutComponent
									size={150}
									percentage={meeting?.meeting_quorum_percentage_data.attendance_percentage}
									data={[
										meeting?.meeting_quorum_percentage_data.attendance_percentage!,
										100 - meeting?.meeting_quorum_percentage_data.attendance_percentage!,
									]}
									colors={[meeting?.meeting_quorum_percentage_data.is_quorum_met ? '#78d12f' : '#e5252a', '#dedede']}
									header={
										<h6 className={`mb-0 fw-medium ${meeting?.meeting_quorum_percentage_data.is_quorum_met ? 'text-success' : 'text-danger'}`}>
											{meeting?.meeting_quorum_percentage_data.is_quorum_met ? t('ACTIONS.MEETING_QUORUM_MET') : t('ACTIONS.MEETING_QUORUM_NOT_MET')}
										</h6>
									}
								/>
							</Col>

							{(!meeting?.location_type.includes('Physical') || meeting?.location_type === 'Both') && <Divider className="my-0" />}

							{(meeting?.location_type.includes('Physical') || meeting?.location_type === 'Both') && (
								<Col span={24}>
									<div className="my-4">
										<MapComponent lat={+meeting?.physical_lat!} lng={+meeting?.physical_lng!} disabled />

										<div className="mt-3">
											<DisabledElement fullWidth label={t('ACTIONS.ADDRESS')} value={meeting?.physical_address} />

											<DisabledElement fullWidth label={t('ACTIONS.ADDRESS_DESC')} value={meeting?.physical_address_description} />
										</div>
									</div>

									<Divider />
								</Col>
							)}

							<Col span={24}>
								<h6 className="mb-3 fw-medium" style={{ fontSize: '1.1rem' }}>
									{t('ACTIONS.ATTENDEES')}
								</h6>

								<Table
									columns={attendeesColumns}
									className="bordered"
									dataSource={attendees}
									scroll={{ x: 600 }}
									rowKey="id"
									loading={isLoading}
									pagination={{
										pageSize: 15,
										total: totalAttendeesRecords,
										showSizeChanger: false,
										current: currentAttendeePage,
										onChange: getAttendees,
										showTotal: (total) => <TableTotal total={total} />,
									}}
									locale={{ emptyText: <NoData /> }}
								/>

								<Divider />
							</Col>

							<Col span={24}>
								<h6 className="mb-3 fw-medium" style={{ fontSize: '1.1rem' }}>
									{t('ACTIONS.MEETING_AGENDA')}
								</h6>

								{meeting?.agendas.map((agenda, index) => (
									<FilePreview key={index} name={agenda.title} downloadLink={agenda.download_link} className="mb-3" />
								))}
							</Col>

							<Divider />

							<Col span={24}>
								<h6 className="mb-3 fw-medium" style={{ fontSize: '1.1rem' }}>
									{t('ACTIONS.DECISIONS')}
								</h6>

								{meeting?.decisions.map((decision, index) => (
									<Row key={index} className="mb-5" gutter={[16, 16]}>
										<Col span={24}>
											<Flex justify="center" vertical className="h-100">
												<DisabledElement
													label={t('ACTIONS.DECISION') + ` #${index + 1}`}
													value={isRTL() ? decision.translated_title.ar : (decision.translated_title.en ?? decision.translated_title.ar)}
												/>
											</Flex>
										</Col>

										<Col span={24} md={9}>
											<DisabledElement
												fullWidth
												label={t('ACTIONS.ABSTENTION_TYPE')}
												value={t(`ACTIONS.${decision?.abstention_type?.toUpperCase()}`)}
											/>
										</Col>

										<Col span={24} md={15}>
											{decision?.abstention_type == 'specific_committee_members' && (
												<Flex
													gap={10}
													wrap
													style={{
														background: '#f9fafb',
														border: '1px solid #d9d9d9',
														padding: '5px 11px',
														borderRadius: 'var(--radius)',
														maxWidth: 'unset',
														marginTop: 28,
													}}>
													{decision?.abstaining_committee_members.map((shareholder, index) => (
														<p
															className="mb-0"
															style={{ fontSize: 12, background: '#f2f2f2', padding: '5px 10px', borderRadius: 5, fontWeight: 500 }}
															key={index}>
															{`${isRTL() ? shareholder.name : (shareholder.name_en ?? shareholder.name)}`}
														</p>
													))}
												</Flex>
											)}
										</Col>

										<Col span={24} className="mt-4">
											<Row gutter={[16, 16]}>
												<Col span={24} lg={14}>
													<h6 style={{ textAlign: 'center', fontSize: '1.1rem' }}>{t('ACTIONS.CURRENT_VOTING_RESULT')}</h6>

													<Flex justify="space-around" align="center" gap={20} wrap style={{ marginBlock: 20, borderInlineEnd: '1px solid #ccc' }}>
														<DoughnutComponent
															percentage={decision.voting_result_details?.Yes}
															data={[+decision.voting_result_details?.Yes!, 100 - +decision.voting_result_details?.Yes!]}
															colors={['#78d12f', '#dedede']}
															footer={t('ACTIONS.YES')}
														/>

														<DoughnutComponent
															percentage={decision.voting_result_details?.No}
															data={[+decision.voting_result_details?.No!, 100 - +decision.voting_result_details?.No!]}
															colors={['#ee1e40', '#dedede']}
															footer={t('ACTIONS.NO')}
														/>

														<DoughnutComponent
															percentage={decision.voting_result_details?.Abstained}
															data={[+decision.voting_result_details?.Abstained, 100 - +decision.voting_result_details?.Abstained!]}
															colors={['#2884EF', '#dedede']}
															footer={t('ACTIONS.ABSTAINED')}
														/>

														<DoughnutComponent
															percentage={decision.voting_result_details?.Dissent}
															data={[+decision.voting_result_details?.Dissent, 100 - +decision.voting_result_details?.Dissent!]}
															colors={['#2884EF', '#dedede']}
															footer={t('ACTIONS.DISSENT')}
														/>
													</Flex>
												</Col>
												<Col span={24} lg={10}>
													<h6 style={{ textAlign: 'center', fontSize: '1.1rem' }}>{t('ACTIONS.ATTENDANCE_VOTING_STATUS')}</h6>

													<Flex justify="space-around" align="center" gap={20} style={{ marginBlock: 20 }}>
														<DoughnutComponent
															percentage={decision.voting_result_details?.VoteParticipationPercentage}
															data={[
																+decision.voting_result_details?.VoteParticipationPercentage!,
																100 - +decision.voting_result_details?.VoteParticipationPercentage!,
															]}
															colors={['#78d12f', '#dedede']}
															footer={
																<div className="text-center">
																	<p className="mb-1">{t('ACTIONS.VOTED_USERS')}</p>
																	<p className={`mb-0 fw-medium text-success`}>
																		{t('ACTIONS.NUMBER_OF_MEMBER_ADDED_VOTES')}: {decision.voting_result_details?.VotedMembersCount}{' '}
																		{t('ACTIONS.MEMBERS')}
																	</p>
																</div>
															}
														/>

														<DoughnutComponent
															percentage={decision.voting_result_details?.NoVotePercentage}
															data={[+decision.voting_result_details?.NoVotePercentage!, 100 - +decision.voting_result_details?.NoVotePercentage!]}
															colors={['#e5252a', '#dedede']}
															footer={
																<div className="text-center">
																	<p className="mb-1">{t('ACTIONS.NO_VOTE')}</p>
																	<p className={`mb-0 fw-medium text-danger`}>
																		{t('ACTIONS.NUMBER_OF_MEMBER_ADDED_NO_VOTES')}: {decision.voting_result_details?.NotVotedMembersCount}{' '}
																		{t('ACTIONS.MEMBERS')}
																	</p>
																</div>
															}
														/>
													</Flex>
												</Col>
											</Row>
										</Col>
									</Row>
								))}
							</Col>

							<Col span={24}>
								{(meeting?.attendance_pdf_path?.download_link ||
									meeting?.decisions_comments_pdf_path?.download_link ||
									meeting?.decisions_results_pdf_path?.download_link ||
									meeting?.member_voting_cards_pdf_path?.download_link) && (
									<>
										<Divider />

										<h6 className="mb-3 fw-medium" style={{ fontSize: '1.1rem' }}>
											{t('ACTIONS.MEETING_FILES')}
										</h6>
									</>
								)}

								{meeting?.attendance_pdf_path?.download_link && (
									<FilePreview
										className="mb-3"
										name={meeting?.attendance_pdf_path.title!}
										downloadLink={meeting?.attendance_pdf_path.download_link!}
									/>
								)}

								{meeting?.decisions_comments_pdf_path?.download_link && (
									<FilePreview
										className="mb-3"
										name={meeting?.decisions_comments_pdf_path.title!}
										downloadLink={meeting?.decisions_comments_pdf_path.download_link!}
									/>
								)}

								{meeting?.decisions_results_pdf_path?.download_link && (
									<FilePreview
										className="mb-3"
										name={meeting?.decisions_results_pdf_path.title!}
										downloadLink={meeting?.decisions_results_pdf_path.download_link!}
									/>
								)}

								{meeting?.member_voting_cards_pdf_path?.download_link && (
									<FilePreview
										className="mb-3"
										name={meeting?.member_voting_cards_pdf_path.title!}
										downloadLink={meeting?.member_voting_cards_pdf_path.download_link!}
									/>
								)}
							</Col>

							<Col span={24}>
								<Flex justify="flex-end" align="center" gap={20} className="mt-5">
									{meeting?.can_close_voting && (
										<Flex gap={30} align="center">
											<Flex gap={10} align="center">
												<h6 className="mb-0 fw-medium">{t('ACTIONS.VOTING_DURATION')}</h6>
												<h6 className="mb-0 fw-medium">
													{String(votingHours).padStart(2, '0')} : {String(votingMinutes).padStart(2, '0')} : {String(votingSeconds).padStart(2, '0')}
												</h6>
											</Flex>

											<Button type="primary" danger size="middle" onClick={() => setCloseVoteModalOpen(true)}>
												{t('ACTIONS.END_VOTING')}
											</Button>
										</Flex>
									)}
									{meeting?.can_close_meeting && (
										<Flex gap={30} align="center">
											<Flex gap={10} align="center">
												<h6 className="mb-0 fw-medium">{t('ACTIONS.MEETING_DURATION')}</h6>
												<h6 className="mb-0 fw-medium">
													{String(meetingHours).padStart(2, '0')} : {String(meetingMinutes).padStart(2, '0')} :
													{String(meetingSeconds).padStart(2, '0')}
												</h6>
											</Flex>

											<Button type="primary" danger size="middle" onClick={() => setEndMeetingModalOpen(true)}>
												{t('ACTIONS.END_MEETING')}
											</Button>
										</Flex>
									)}

									{meeting?.can_publish && (
										<Button type="primary" size="middle" onClick={publishHandler} loading={isPublishing}>
											{t('ACTIONS.PUBLISH')}
										</Button>
									)}

									{meeting?.status == 'WritingMinutes' && (
										<Button
											type="primary"
											size="middle"
											onClick={() => navigate(`/dashboard/actions/boards/committees/${committeeId!}/meetings/${meeting?.id}/decision-comments`)}>
											{meeting?.decisions.findIndex((d) => d.committee_comment != null) > -1
												? t('ACTIONS.EDIT_DECISION_COMMENTS')
												: t('ACTIONS.ADD_DECISION_COMMENTS')}
										</Button>
									)}
								</Flex>
							</Col>
						</Row>
					</>
				)}
			</Card>

			{/* END VOTING MODAL */}
			<ConfirmModalComponent
				open={closeVoteModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={closeVoteHandler}
				isLoading={isSubmitting}
				onDiscard={() => setCloseVoteModalOpen(false)}
				title={t('ACTIONS.CLOSE_VOTING')}
				description={t('ACTIONS.CONFIRM_CLOSE_VOTING')}
				descriptionClasses="text-center"
			/>

			{/* END MEETING MODAL */}
			<ConfirmModalComponent
				open={endMeetingModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={endMeetingHandler}
				isLoading={isSubmitting}
				onDiscard={() => setEndMeetingModalOpen(false)}
				title={t('ACTIONS.END_MEETING')}
				description={t('ACTIONS.CONFIRM_END_MEETING')}
				descriptionClasses="text-center"
			/>
		</>
	);
};

export default CommitteeMeetingViewPage;
